import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { useListAdminsQuery } from '../../features/admin/adminAccountsApi';
import { Skeleton } from '../ui/Skeleton';

/**
 * Type-to-search picker over the live GET /admin/admins list — the only
 * real-backend endpoint anywhere in the app that returns {email, uuid}
 * pairs to search against. Selecting a result emits that account's uuid
 * via onChange; typing without picking a match clears the committed value
 * so the form can't submit a stale/mismatched uuid.
 *
 * Deliberately scoped to admin accounts only — there's no endpoint yet
 * that lists investors (Investors is still "not yet populated" per the
 * docs), so this can't be the *only* way to target an account. See the
 * "Enter UUID manually" fallback in UuidActionForm for investor targets.
 */
export default function AdminEmailAutocomplete({ value, onChange, error, label = 'Administrator', placeholder = 'Start typing a name, email, or UUID…' }) {
  const { data, isLoading } = useListAdminsQuery();
  const admins = data ?? [];
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  // Keep the visible text in sync when `value` is set/cleared from outside
  // (e.g. form reset after a successful submit).
  useEffect(() => {
    if (!value) {
      setQuery('');
      return;
    }
    const match = admins.find((a) => a.id === value);
    if (match) setQuery(`${match.full_name} — ${match.email}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const filtered = query.trim()
    ? admins.filter((a) => {
        const q = query.trim().toLowerCase();
        return (
          a.email.toLowerCase().includes(q) ||
          a.full_name.toLowerCase().includes(q) ||
          (a.id && a.id.toLowerCase().includes(q))
        );
      })
    : admins;

  const select = (admin) => {
    onChange(admin.id);
    setQuery(`${admin.full_name} — ${admin.email}`);
    setOpen(false);
  };

  const handleTyping = (e) => {
    setQuery(e.target.value);
    onChange(''); // clear the committed uuid until a real match is picked again
    setOpen(true);
  };

  return (
    <div className="relative" ref={containerRef}>
      {label && <label className="block text-sm font-medium text-foreground-muted mb-1.5">{label}</label>}
      <input
        type="text"
        value={query}
        onChange={handleTyping}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        autoComplete="off"
        className={clsx(
          'w-full rounded-xl bg-background-elevated border px-3.5 py-2.5 text-sm text-foreground',
          'placeholder:text-foreground-subtle transition-colors',
          'focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent',
          error ? 'border-danger' : 'border-border focus:border-border-strong'
        )}
      />
      {error && <p className="mt-1.5 text-xs text-danger">{error}</p>}

      {open && (
        <div className="absolute left-0 right-0 top-full mt-1.5 max-h-64 overflow-y-auto rounded-xl border border-border bg-background-surface shadow-card-hover z-30 py-1">
          {isLoading && (
            <div className="px-3 py-2 space-y-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-8 w-full" />
              ))}
            </div>
          )}
          {!isLoading && filtered.length === 0 && (
            <p className="px-3 py-3 text-sm text-foreground-muted text-center">No matching administrators.</p>
          )}
          {!isLoading &&
            filtered.map((a) => (
              <button
                key={a.id}
                type="button"
                onClick={() => select(a)}
                className="w-full flex flex-col items-start px-3 py-2 text-left hover:bg-background-elevated transition-colors"
              >
                <span className="text-sm text-foreground">{a.full_name}</span>
                <span className="text-xs text-foreground-subtle">{a.email}</span>
                <span className="text-[10px] text-foreground-subtle/70 font-mono">{a.id}</span>
              </button>
            ))}
        </div>
      )}
    </div>
  );
}
